import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Sparkles, Check, Zap, Package, Eye,
  Wand2, Compass, Rocket,
} from 'lucide-react';

const BRAND = {
  navy: '#03183D',
  deepBlue: '#0A2E5D',
  teal: '#16AAA8',
  cream: '#F7F9FC',
  amber: '#F5A623',
};

const PATHS = [
  {
    icon: Zap,
    tint: 'from-emerald-500 to-teal-600',
    accent: 'text-emerald-700',
    bg: 'bg-emerald-50',
    name: 'Fast Cash',
    tagline: 'Sell a service this week',
    detail: 'Package your skill into a simple service, price it right, and get your first paying client using ready-to-send outreach scripts.',
  },
  {
    icon: Package,
    tint: 'from-violet-500 to-indigo-600',
    accent: 'text-violet-700',
    bg: 'bg-violet-50',
    name: 'Digital Product',
    tagline: 'Turn your knowledge into an ebook, workbook, or template',
    detail: 'Go from a rough idea to a finished product with an outline, cover concept, sales page, and 5-email launch sequence.',
  },
  {
    icon: Eye,
    tint: 'from-amber-500 to-orange-600',
    accent: 'text-amber-700',
    bg: 'bg-amber-50',
    name: 'Faceless Affiliate',
    tagline: 'Earn as a recommender, no camera needed',
    detail: 'Pick a proven offer, build a bridge page, and generate content that quietly sends buyers your way — all without showing your face.',
  },
];

const HOW_STEPS = [
  {
    icon: Compass,
    title: 'Pick your path',
    body: 'Answer a few questions and Creataby picks the fastest path to your first result — Fast Cash, Digital Product, or Faceless Affiliate.',
  },
  {
    icon: Wand2,
    title: 'Follow guided steps',
    body: 'Every step is one clear action. AI writes the copy, outlines the product, and scripts the outreach so you just tweak and ship.',
  },
  {
    icon: Rocket,
    title: 'Launch and sell',
    body: 'Export your assets to the tools you already use — ClickFunnels, Systeme.io, your email list — and watch the first sales roll in.',
  },
];

const PRICING_PREVIEW = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: ['2 generations per month', '1 active project', 'All 3 income paths'],
    cta: 'Start Free',
    href: '/login',
    highlight: false,
  },
  {
    name: 'Creator',
    price: '$39',
    period: '/month',
    features: ['Unlimited generations', 'Up to 3 active projects', 'PDF export + cover concepts', '5-email launch sequence'],
    cta: 'Start with Creator',
    href: '/pricing',
    highlight: false,
  },
  {
    name: 'Creator Pro',
    price: '$79',
    period: '/month',
    features: ['Everything in Creator', 'Unlimited projects', 'Advanced graphics + launch campaigns', 'ClickFunnels & Systeme.io export'],
    cta: 'Go Pro',
    href: '/pricing',
    highlight: true,
  },
];

const Landing = () => {
  const { session, isOnboarded, loading } = useAuth();

  // Authenticated users skip the marketing splash and go straight into the app.
  if (!loading && session) {
    return <Navigate to={isOnboarded ? '/dashboard' : '/onboarding'} replace />;
  }

  return (
    <div className="min-h-screen bg-white" style={{ color: BRAND.deepBlue }}>
      {/* Top Nav */}
      <header
        className="sticky top-0 z-40 border-b border-slate-100 backdrop-blur-md"
        style={{ backgroundColor: 'rgba(255,255,255,0.85)' }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-nav-logo">
            <img src="/banner-logo.png" alt="Creataby" className="h-10" />
          </Link>
          <nav className="flex items-center gap-1 sm:gap-3">
            <Link to="/docs">
              <Button
                variant="ghost"
                size="sm"
                className="text-sm text-slate-600 hover:text-slate-900"
                data-testid="landing-nav-guides"
              >
                Guides
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                variant="ghost"
                size="sm"
                className="text-sm text-slate-600 hover:text-slate-900"
                data-testid="landing-nav-pricing"
              >
                Pricing
              </Button>
            </Link>
            <Link to="/login">
              <Button
                variant="ghost"
                size="sm"
                className="text-sm text-slate-600 hover:text-slate-900"
                data-testid="landing-nav-signin"
              >
                Sign in
              </Button>
            </Link>
            <Link to="/login">
              <Button
                size="sm"
                className="text-sm font-semibold rounded-full px-4 shadow-[0_2px_10px_rgba(22,170,168,0.35)] transition-all hover:shadow-[0_4px_18px_rgba(22,170,168,0.45)]"
                style={{ backgroundColor: BRAND.teal, color: 'white' }}
                data-testid="landing-nav-cta"
              >
                Start free <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Ambient glow */}
        <div
          aria-hidden
          className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
          style={{ background: 'radial-gradient(closest-side, #16AAA8, transparent)' }}
        />
        <div
          aria-hidden
          className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full blur-3xl opacity-25"
          style={{ background: 'radial-gradient(closest-side, #F5A623, transparent)' }}
        />

        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center relative">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full mb-5"
              style={{ backgroundColor: 'rgba(22,170,168,0.10)', color: BRAND.teal }}
              data-testid="landing-hero-badge"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Creation made simple
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-[1.05] tracking-tight mb-5"
              style={{ color: BRAND.deepBlue }}
              data-testid="landing-hero-title"
            >
              Turn what you know into something{' '}
              <span className="relative inline-block">
                <span style={{ color: BRAND.amber }}>you can sell.</span>
                <svg
                  className="absolute -bottom-1 left-0 w-full"
                  height="10"
                  viewBox="0 0 200 10"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <path
                    d="M2 7 Q 60 -1, 120 5 T 198 4"
                    stroke={BRAND.amber}
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                    opacity="0.6"
                  />
                </svg>
              </span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-xl leading-relaxed">
              Creataby guides you from a rough idea to a finished digital product and the materials
              you need to launch it — one simple step at a time.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link to="/login">
                <Button
                  size="lg"
                  className="text-base font-semibold rounded-full px-6 h-12 shadow-[0_4px_18px_rgba(22,170,168,0.4)] transition-all hover:shadow-[0_8px_28px_rgba(22,170,168,0.55)] hover:-translate-y-0.5"
                  style={{ backgroundColor: BRAND.teal, color: 'white' }}
                  data-testid="landing-hero-cta-primary"
                >
                  Start free <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  variant="outline"
                  size="lg"
                  className="text-base font-semibold rounded-full px-6 h-12 border-slate-300 hover:bg-slate-50"
                  style={{ color: BRAND.deepBlue }}
                  data-testid="landing-hero-cta-secondary"
                >
                  See pricing
                </Button>
              </Link>
            </div>
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <Check className="h-4 w-4" style={{ color: BRAND.teal }} />
              Free plan includes 2 generations/month. No credit card required.
            </p>
          </motion.div>

          {/* Splash Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="relative"
          >
            <div
              className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(3,24,61,0.25)] border border-white/20"
              style={{ backgroundColor: BRAND.navy }}
            >
              <img
                src="/creataby-splash.webp"
                alt="From idea to sellable product"
                className="w-full h-auto"
                style={{ objectFit: 'cover' }}
                data-testid="landing-hero-image"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PICK YOUR PATH */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="max-w-2xl mb-10">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] mb-2" style={{ color: BRAND.teal }}>
            Three ways to earn
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: BRAND.deepBlue }}>
            Pick the path that matches your energy.
          </h2>
          <p className="text-slate-600 mt-3">
            Not sure? A 60-second quiz picks one for you after you sign up.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PATHS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="rounded-2xl bg-white border border-slate-100 p-6 shadow-[0_6px_24px_rgba(3,24,61,0.05)] transition-all hover:shadow-[0_12px_40px_rgba(3,24,61,0.1)]"
                data-testid={`landing-path-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${p.tint} flex items-center justify-center mb-4 shadow-md`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-1" style={{ color: BRAND.deepBlue }}>{p.name}</h3>
                <p className={`text-sm font-semibold ${p.accent} mb-3`}>{p.tagline}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{p.detail}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-16 sm:py-24" style={{ backgroundColor: BRAND.cream }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-[0.12em] mb-2" style={{ color: BRAND.teal }}>
              How it works
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: BRAND.deepBlue }}>
              A guided path, not a blank page.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-10">
            {HOW_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="relative"
                >
                  <div
                    className="text-[80px] font-black leading-none mb-3 opacity-15"
                    style={{ color: BRAND.deepBlue }}
                  >
                    0{i + 1}
                  </div>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 shadow-sm"
                       style={{ backgroundColor: 'white', border: `1.5px solid ${BRAND.teal}` }}>
                    <Icon className="h-5 w-5" style={{ color: BRAND.teal }} />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: BRAND.deepBlue }}>{s.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{s.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="max-w-2xl mx-auto text-center mb-10">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] mb-2" style={{ color: BRAND.teal }}>
            Simple pricing
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" style={{ color: BRAND.deepBlue }}>
            Start free. Upgrade only when you need more.
          </h2>
          <p className="text-slate-600">
            Every plan includes all 3 income paths and step-by-step guidance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {PRICING_PREVIEW.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className={`relative rounded-2xl overflow-hidden transition-all ${
                plan.highlight
                  ? 'shadow-[0_16px_44px_rgba(14,129,117,0.28)] ring-1 ring-teal-500/30'
                  : 'border border-slate-100 shadow-[0_4px_18px_rgba(3,24,61,0.06)]'
              }`}
              data-testid={`landing-pricing-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {plan.highlight ? (
                <div className="px-6 pt-6 pb-5 bg-gradient-to-br from-teal-600 to-emerald-700 text-white">
                  <p className="text-[11px] font-bold uppercase tracking-wider bg-white/25 inline-block px-2 py-0.5 rounded-full mb-3">
                    Most Popular
                  </p>
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-white/80 text-sm">{plan.period}</span>
                  </div>
                </div>
              ) : (
                <div className="px-6 pt-6 pb-5 bg-white">
                  <h3 className="text-lg font-bold" style={{ color: BRAND.deepBlue }}>{plan.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-4xl font-bold" style={{ color: BRAND.deepBlue }}>{plan.price}</span>
                    <span className="text-sm text-slate-500">{plan.period}</span>
                  </div>
                </div>
              )}
              <div className="bg-white px-6 pt-5 pb-6">
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: BRAND.teal }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={plan.href}>
                  <Button
                    className={`w-full font-semibold rounded-full ${
                      plan.highlight
                        ? 'bg-creataby-gradient-btn text-white hover:opacity-95'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                    data-testid={`landing-pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link to="/pricing" className="text-sm font-semibold inline-flex items-center gap-1.5 hover:underline" style={{ color: BRAND.teal }}>
            Compare full features <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-20 sm:py-24" style={{ backgroundColor: BRAND.navy }}>
        <div
          aria-hidden
          className="absolute inset-0 opacity-40"
          style={{
            background: 'radial-gradient(60% 60% at 50% 40%, rgba(22,170,168,0.35), transparent 70%)',
          }}
        />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-5xl font-bold tracking-tight text-white mb-4"
            data-testid="landing-final-cta-title"
          >
            Your idea is waiting.
          </motion.h2>
          <p className="text-lg text-slate-300 mb-8">
            Sign up in 30 seconds and take your first step today — no credit card, no risk.
          </p>
          <Link to="/login">
            <Button
              size="lg"
              className="text-base font-semibold rounded-full px-8 h-12 shadow-[0_6px_24px_rgba(22,170,168,0.6)] hover:-translate-y-0.5 transition-all"
              style={{ backgroundColor: BRAND.teal, color: 'white' }}
              data-testid="landing-final-cta-button"
            >
              Let's keep building <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/banner-logo.png" alt="Creataby" className="h-8" />
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/pricing" className="hover:text-slate-900">Pricing</Link>
            <Link to="/docs" className="hover:text-slate-900">Guides</Link>
            <Link to="/login" className="hover:text-slate-900">Sign in</Link>
            <a href="mailto:support@creataby.com" className="hover:text-slate-900">Contact</a>
          </div>
          <p className="text-xs text-slate-400">
            &copy; {new Date().getFullYear()} Creataby. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
